import type { FC } from 'react';
import { useEffect, useRef } from 'react';
import { useSystemTheme } from '@/ui/hooks/useSystemTheme';
import { rgba } from 'polished';
import { useShakeIntensity } from './useShakeIntensity';
import { ECGBackground } from './ECGBackground/ECGBackground';
import { heartBeatRhythm } from './heartBeatRhythm';
import styles from './ECGChart.module.css';

interface ECGChartProps {
  heartRate?: number;
}

/**
 * One point is a number from 0 to 1 indicating the height of the line at that point
 * stretched across the height of the canvas
 */
type Point = number;
export const ECGChart: FC<ECGChartProps> = ({ heartRate = 0 }) => {
  const theme = useSystemTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const width = 100;
  const height = 32;
  const pointSize = 2;
  const pointSpacing = 0.5;
  const maxPoints = Math.ceil(width / pointSpacing);

  const points = useRef<Point[]>([]);

  const shakeIntensityRef = useShakeIntensity();

  const strokeStyle =
    heartRate === 0 ? rgba(theme.foreground, 0.5) : theme.accent;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const devicePixelRatio = window.devicePixelRatio || 1;
    canvas.width = width * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    ctx.scale(devicePixelRatio, devicePixelRatio);

    const targetSampleRate = 120;
    const msPerPoint = 1000 / targetSampleRate;
    let lastPointTimestamp = Date.now();

    const generatePoint = (timestamp: number) => {
      const shakeNoise = shakeIntensityRef.current * 0.5;
      const magnitude = Math.random() * 0.1;
      const frequency = 1000;
      const noise =
        (Math.sin(timestamp / frequency) + 1 * shakeNoise) * magnitude * 0.5;

      const point = heartBeatRhythm(timestamp, heartRate) + noise;
      return point;
    };

    const addPoints = (currentTime: number) => {
      const elapsed = currentTime - lastPointTimestamp;
      const pointsToAdd = Math.floor(elapsed / msPerPoint);

      if (pointsToAdd > 0) {
        const firstPoint = Math.max(0, pointsToAdd - maxPoints);
        for (let i = firstPoint; i < pointsToAdd; i++) {
          const pointTimestamp = lastPointTimestamp + (i + 1) * msPerPoint;
          points.current.push(generatePoint(pointTimestamp));
        }
        lastPointTimestamp += pointsToAdd * msPerPoint;

        if (points.current.length > maxPoints) {
          points.current = points.current.slice(-maxPoints);
        }
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      ctx.beginPath();
      ctx.strokeStyle = strokeStyle;
      ctx.lineCap = 'round';
      for (let i = points.current.length - 1; i >= 0; i--) {
        const reverseIndex = points.current.length - 1 - i;
        ctx.lineTo(
          width - reverseIndex * pointSpacing - pointSize,
          (points.current[i] * height) / 2
        );
      }
      ctx.stroke();

      if (points.current.length > 0) {
        ctx.beginPath();
        ctx.arc(
          width - pointSize,
          (points.current[points.current.length - 1] * height) / 2,
          pointSize,
          0,
          2 * Math.PI
        );
        ctx.fillStyle = strokeStyle;
        ctx.fill();
      }
    };

    let animationFrame: number;
    const animate = () => {
      const currentTime = Date.now();
      addPoints(currentTime);
      draw();
      animationFrame = requestAnimationFrame(animate);
    };
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- possibly stale: the rAF loop closes over the initial heartRate, see PR
  }, []);

  return (
    <div className={styles.chart}>
      <ECGBackground
        width={width}
        height={height}
        pointSize={pointSize}
        strokeStyle={strokeStyle}
      />

      <canvas ref={canvasRef} className={styles.trace} />
    </div>
  );
};
